import { CreditCard, KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import EnhancedTable from 'lib/designSystem/Table';
import {
  CreditLine,
  CreditLineConcentration,
  SubProductCreditLineConcentration,
} from 'lib/types/models';
import React, { useState } from 'react';

interface Props {
  creditLineInfo: CreditLine;
  concentrations: {
    stakeholderAssignations: CreditLineConcentration[];
    subProductAssignations: SubProductCreditLineConcentration[];
  };
}

const CreditLineData = (props: Props) => {
  const { creditLineInfo, concentrations } = props;
  const [concentrationType, setConcentrationType] = useState('stakeholder');
  const [showDetails, setShowDetails] = useState(false);

  return (
    <>
      <Paper
        onClick={() => setShowDetails(!showDetails)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setShowDetails(!showDetails);
        }}
        sx={{
          p: 2,
          mt: 2,
          mb: 1,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'nowrap',
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#fafafa' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 160,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(25, 118, 210, 0.12)',
              borderRadius: '50%',
              p: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CreditCard sx={{ fontSize: '1rem', color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                lineHeight: 1,
                fontSize: { xs: '0.95rem', md: '1.05rem' },
              }}
            >
              Línea de Crédito
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.75rem', lineHeight: 1, mt: 0.2 }}
            >
              Resumen
            </Typography>
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {creditLineInfo ? (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 110,
              }}
            >
              <Stack spacing={0.25} alignItems="flex-start">
                <Typography variant="caption" color="text.secondary">
                  MONTO
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  ${creditLineInfo.amount?.toLocaleString() || 'N/A'}
                </Typography>
              </Stack>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 110,
              }}
            >
              <Stack spacing={0.25} alignItems="flex-start">
                <Typography variant="caption" color="text.secondary">
                  VENCIMIENTO
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {creditLineInfo.validUntil
                    ? new Date(creditLineInfo.validUntil).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Stack>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 110,
              }}
            >
              <Stack spacing={0.25} alignItems="flex-start">
                <Typography variant="caption" color="text.secondary">
                  CONTRATO
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {creditLineInfo.contractDueDate
                    ? new Date(
                        creditLineInfo.contractDueDate,
                      ).toLocaleDateString()
                    : 'Sin contrato'}
                </Typography>
              </Stack>
            </Box>

            {/* Observaciones movidas al colapsable */}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
              <KeyboardArrowDown
                sx={{
                  transition: 'transform 0.2s ease',
                  transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: 'text.secondary',
                }}
              />
            </Box>
          </>
        ) : (
          <Chip
            label="Información no disponible"
            color="warning"
            size="small"
            variant="outlined"
          />
        )}
      </Paper>

      <Collapse in={showDetails} timeout="auto" unmountOnExit>
        <Paper
          sx={{ p: 2, mb: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}
        >
          <Stack spacing={1.5}>
            {creditLineInfo?.observations && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Observaciones
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}
                  color="text.primary"
                >
                  {creditLineInfo.observations}
                </Typography>
                <Divider sx={{ mt: 1 }} />
              </Box>
            )}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Concentraciones
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={concentrationType}
                exclusive
                onChange={(_, val) => val && setConcentrationType(val)}
              >
                <ToggleButton
                  value="stakeholder"
                  sx={{ textTransform: 'none' }}
                >
                  Deudor
                </ToggleButton>
                <ToggleButton value="subproduct" sx={{ textTransform: 'none' }}>
                  Subproductos
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {concentrationType === 'stakeholder' ? (
              <EnhancedTable
                dataHeaders={[
                  { key: 'businessName', label: 'Nombre' },
                  { key: 'businessIdentifier', label: 'Rut' },
                  { key: 'amount', label: 'Monto', type: 'money' },
                ]}
                data={concentrations.stakeholderAssignations || []}
              />
            ) : (
              <EnhancedTable
                dataHeaders={[
                  { key: 'name', label: 'Producto' },
                  { key: 'advance', label: 'Anticipo' },
                  { key: 'spread', label: 'Spread' },
                  { key: 'arrearRate', label: 'Tasa mora' },
                  { key: 'maxFinancingDays', label: 'Días' },
                  {
                    key: 'operationCostFixed',
                    label: 'C. fijo',
                    type: 'money',
                  },
                  { key: 'operationCostVariable', label: 'C. variable' },
                  { key: 'amount', label: 'Monto', type: 'money' },
                ]}
                data={concentrations.subProductAssignations || []}
              />
            )}
          </Stack>
        </Paper>
      </Collapse>
    </>
  );
};

export default CreditLineData;
